import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { FocusSource, useComponentFocus } from '@/hooks/use-component-focus';
import { gql } from '@apollo/client';
import {
  ItemLeadTimeContext_GetResolvedLeadTimeFulfillmentQuery,
  ItemLeadTimeContext_GetResolvedLeadTimeFulfillmentQueryVariables,
} from '@/types';
import { useDynamicConfigValue } from '@/hooks/use-dynamic-config-value';
import { useNewOrderActionsContext } from './new-order-actions-context';
import { useNewOrderContext } from './new-order-context';
import { useWrappedQuery } from '@/hooks/use-wrapped-query';

export const GET_RESOLVED_LEAD_TIME_FULFILLMENT_QUERY = gql`
  query ItemLeadTimeContext_GetResolvedLeadTimeFulfillment(
    $desiredFulfillmentDate: String
    $fulfillmentSettingsUUID: uuid!
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
  ) {
    getResolvedLeadTimeFulfillment(
      desired_fulfillment_date: $desiredFulfillmentDate
      fulfillment_settings_id: $fulfillmentSettingsUUID
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
    ) {
      settings {
        earliest_fulfillment_at
      }
      variant_pack_ids
    }
  }
`;

interface LeadTimeFulfillmentMap {
  [variantPackUUID: string]: string | null;
}

export interface ItemLeadTimeContextType {
  leadTimeFulfillmentEnabled: boolean;
  leadTimeFulfillmentMap: LeadTimeFulfillmentMap;
  variantPackHasLeadTimePolicy: (variantPackUUID: string) => boolean;
}

export const ItemLeadTimeContext =
  createContext<ItemLeadTimeContextType | null>(null);

export const useItemLeadTimeContext = () => {
  const context = useContext(ItemLeadTimeContext);
  if (!context) {
    throw new Error(
      'useItemLeadTimeContext requires a ItemLeadTimeContextProvider',
    );
  }

  return context;
};

interface Props {
  children?: React.ReactNode;
}

export const ItemLeadTimeContextProvider: React.FC<Props> = ({ children }) => {
  const { restaurantUUID, supplierUUID } = useNewOrderContext();
  const { fulfillmentHydrated, selectedFulfillment } =
    useNewOrderActionsContext();

  const leadTimeFulfillmentEnabled = Boolean(
    useDynamicConfigValue('item_lead_time_settings_enabled'),
  );

  const fulfillmentSettingsUUID = selectedFulfillment?.fulfillmentSettingsUUID;
  const fulfillmentDate = selectedFulfillment?.date;
  const skipQuery =
    !leadTimeFulfillmentEnabled ||
    !fulfillmentSettingsUUID ||
    !restaurantUUID ||
    !supplierUUID;

  const { data, refetch } = useWrappedQuery<
    ItemLeadTimeContext_GetResolvedLeadTimeFulfillmentQuery,
    ItemLeadTimeContext_GetResolvedLeadTimeFulfillmentQueryVariables
  >(GET_RESOLVED_LEAD_TIME_FULFILLMENT_QUERY, {
    fetchPolicy: 'no-cache',
    skip: skipQuery || !fulfillmentHydrated,
    variables: {
      desiredFulfillmentDate: fulfillmentDate,
      fulfillmentSettingsUUID: fulfillmentSettingsUUID!,
      restaurantUUID: restaurantUUID!,
      supplierUUID: supplierUUID!,
    },
  });

  // Refetch whenever the app receives focus
  useComponentFocus({
    onFocus: useCallback(
      async (type: FocusSource) => {
        if (type === FocusSource.App && !skipQuery) {
          try {
            await refetch();
          } catch (e) {
            // Do nothing
          }
        }
      },
      [refetch, skipQuery],
    ),
  });

  const value = useMemo<ItemLeadTimeContextType>(() => {
    const leadTimeFulfillmentMap: LeadTimeFulfillmentMap = {};
    for (const resolvedLeadTime of data?.getResolvedLeadTimeFulfillment || []) {
      for (const variantPackUUID of resolvedLeadTime.variant_pack_ids) {
        leadTimeFulfillmentMap[variantPackUUID] =
          resolvedLeadTime.settings.earliest_fulfillment_at ?? null;
      }
    }

    return {
      leadTimeFulfillmentEnabled,
      leadTimeFulfillmentMap,
      variantPackHasLeadTimePolicy: (variantPackUUID: string) =>
        leadTimeFulfillmentEnabled &&
        (!data || variantPackUUID in leadTimeFulfillmentMap),
    };
  }, [data, leadTimeFulfillmentEnabled]);

  return (
    <ItemLeadTimeContext.Provider value={value}>
      {children}
    </ItemLeadTimeContext.Provider>
  );
};

export const DummyItemLeadTimeContextProvider: React.FC<Props> = ({
  children,
}) => {
  const value = {
    leadTimeFulfillmentEnabled: false,
    leadTimeFulfillmentMap: {},
    variantPackHasLeadTimePolicy: () => false,
  };
  return (
    <ItemLeadTimeContext.Provider value={value}>
      {children}
    </ItemLeadTimeContext.Provider>
  );
};
